/**
 * Projects API client hooks using TanStack Query
 * Provides hooks for project management (CRUD operations)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  Project,
  ProjectsListResponse,
  ProjectsFilterParams,
  CreateProjectRequest,
  UpdateProjectRequest,
  EnhancePromptRequest,
  EnhancePromptResponse
} from '@/lib/types'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL resolution with new URL()
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

/**
 * Query key factory for projects
 */
export const projectKeys = {
  all: ['projects'] as const,
  list: (filters: ProjectsFilterParams = {}) =>
    ['projects', 'list', filters] as const,
  detail: (projectId: string) => ['projects', 'detail', projectId] as const
}

/**
 * Fetch projects list from orchestrator API
 */
async function fetchProjects(
  params: ProjectsFilterParams = {}
): Promise<ProjectsListResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/projects', baseUrl)

  if (params.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit))
  }
  if (params.offset !== undefined) {
    url.searchParams.set('offset', String(params.offset))
  }
  if (params.includeArchived !== undefined) {
    url.searchParams.set('includeArchived', String(params.includeArchived))
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch a single project by ID
 */
async function fetchProject(projectId: string): Promise<Project> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/projects/${projectId}`, baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Create a new project
 */
async function createProject(request: CreateProjectRequest): Promise<Project> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/projects', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Update an existing project
 */
async function updateProject({
  projectId,
  request
}: {
  projectId: string
  request: UpdateProjectRequest
}): Promise<Project> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/projects/${projectId}`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Delete a project (soft delete/archive by default)
 */
async function deleteProject({
  projectId,
  hardDelete = false
}: {
  projectId: string
  hardDelete?: boolean
}): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/projects/${projectId}`, baseUrl)

  if (hardDelete) {
    url.searchParams.set('hardDelete', 'true')
  }

  const response = await apiFetch(url.toString(), {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

/**
 * Hook to fetch projects list
 *
 * @param params - Optional filter parameters (limit, offset, includeArchived, searchTerm)
 * @param options - Optional TanStack Query options (enabled, etc.)
 * @returns Query result with projects list
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useProjects({ limit: 10 })
 *
 * // Conditionally disable the query
 * const { data } = useProjects({}, { enabled: shouldFetch })
 * ```
 */
export function useProjects(
  params: ProjectsFilterParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () => fetchProjects(params),
    staleTime: 10000, // 10 seconds - session counts can change frequently
    refetchInterval: 30000, // Poll every 30 seconds to keep session counts fresh
    refetchIntervalInBackground: false, // Don't poll when tab is not visible
    enabled: options.enabled ?? true
  })
}

/**
 * Hook to fetch a single project by ID
 *
 * @param projectId - The project ID to fetch (undefined to disable query)
 * @returns Query result with project data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useProject('project-123')
 * ```
 */
export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(projectId!),
    queryFn: () => fetchProject(projectId!),
    enabled: !!projectId,
    staleTime: 10000, // 10 seconds - session counts can change frequently
    refetchInterval: 30000, // Poll every 30 seconds to keep session counts fresh
    refetchIntervalInBackground: false
  })
}

/**
 * Hook to create a new project
 *
 * @returns Mutation for creating a project
 *
 * @example
 * ```tsx
 * const createProject = useCreateProject()
 * createProject.mutate({ name: 'My Project', description: 'A new project' })
 * ```
 */
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({ queryKey: projectKeys.all })
    }
  })
}

/**
 * Hook to update an existing project
 *
 * @returns Mutation for updating a project
 *
 * @example
 * ```tsx
 * const updateProject = useUpdateProject()
 * updateProject.mutate({
 *   projectId: 'project-123',
 *   request: { name: 'Updated Name' }
 * })
 * ```
 */
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateProject,
    onSuccess: (data, variables) => {
      // Update the specific project in cache
      queryClient.setQueryData(projectKeys.detail(variables.projectId), data)
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({ queryKey: projectKeys.all })
    }
  })
}

/**
 * Hook to delete (archive) a project
 *
 * @returns Mutation for deleting a project
 *
 * @example
 * ```tsx
 * const deleteProject = useDeleteProject()
 * // Soft delete (archive)
 * deleteProject.mutate({ projectId: 'project-123' })
 * // Hard delete (permanent)
 * deleteProject.mutate({ projectId: 'project-123', hardDelete: true })
 * ```
 */
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteProject,
    onSuccess: (_, variables) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: projectKeys.detail(variables.projectId)
      })
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({ queryKey: projectKeys.all })
    }
  })
}

// =============================================================================
// Prompt Enhancement API
// =============================================================================

/**
 * Enhance a task prompt using AI
 * 
 * @param request - The prompt enhancement request
 * @returns Enhanced prompt with constraint suggestions
 */
export async function enhancePrompt(
  request: EnhancePromptRequest
): Promise<EnhancePromptResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/prompts/enhance', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Hook for enhancing prompts with AI
 * 
 * @returns Mutation for enhancing prompts
 * 
 * @example
 * ```tsx
 * const enhancePromptMutation = useEnhancePrompt()
 * 
 * enhancePromptMutation.mutate({
 *   prompt: 'add login feature',
 *   projectId: 'project-123',
 *   constraints: ['quality:tests:Include unit tests']
 * }, {
 *   onSuccess: (data) => {
 *     setPrompt(data.enhancedPrompt)
 *     setSuggestedConstraints(data.suggestedConstraints)
 *   }
 * })
 * ```
 */
export function useEnhancePrompt() {
  return useMutation({
    mutationFn: enhancePrompt
  })
}
